/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react';
import {MediumText, UNSELECTABLE_TEXT} from '../styles/styles';
import {COOL_BLUE, COOLER_GREY, DARK_TOOLTIP, LIGHT_GREY, WHITE} from '../styles/colors';
import * as React from 'react';
import {RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import {useDispatch} from 'react-redux';
import {sortProductsAction} from './sortProductsAction';
import downArrow from '../assets/icons/downArrowSolidBlack.svg';
import {useEffect} from 'react';
import { TooltipFC } from '../layout/TooltipFC';
import infoImg from '../assets/icons/info.svg';
import errorImgRed from '../assets/icons/failureTriangleRed.svg';
import IosSpinner from '../forms/elements/loader/IosSpinner';


const rowItemStyles = css`
  padding: 10px;
  line-height: 22px;
  border: 1px solid transparent;
`;


export const tableStyle = css`
  width: 100%;
  table {
    border-collapse: collapse;
  }

  thead tr {
    ${MediumText};
    align-items: center;
    vertical-align: center;
    text-align: center;
  }

  tbody tr {
    ${MediumText};
    border-color: ${COOLER_GREY};
    background-color: ${WHITE};

    &:nth-of-type(odd) {
      background-color: ${LIGHT_GREY};
    }

    &:last-of-type {
      td {
        border-bottom: 1px solid ${COOLER_GREY};
      }
    }

    &:hover {
      background-color: ${COOL_BLUE};
    }
  }

  th {
    ${rowItemStyles};
    max-width: 200px;
    vertical-align: center;
    text-align: center;
    padding-top: 0;
    border-bottom: 0;
    &:hover {
      cursor: pointer;
    }
  }

  td {
    ${rowItemStyles};
    border-color: ${COOLER_GREY};
    border-right: 0;
    border-bottom: 0;
    height: 100%;

    &:last-of-type {
      border-right: 1px solid ${COOLER_GREY};
    }
  }
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -3px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
  font-size: 16px;
`;

// Fits the message instead of TooltipFC's default 200px
const errorTooltipStyle = css`
  width: max-content;
`;

// Over the triangle's room, matching infoStyle's size and margin. Aligned with the title text, not the taller row.
const columnSpinnerStyle = css`
  position: absolute;
  top: -1px;
  left: 5px;
  width: 20px;
  height: 20px;
`;

// Lifts tableStyle's 200px header cap so the triangle doesn't squeeze the title; !important beats its selector.
const triangleColumnHeaderStyle = css`
  max-width: none !important;
`;

const sortingArrow = (isVisible, sortAscending) => css`
  display: inline-block;
  min-width: 25px;
  visibility: ${isVisible ? 'visible' : 'hidden'};
  transform: ${sortAscending ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: visibility 10ms linear;
  vertical-align: top;
  cursor: pointer;
`;

interface props<T> {
  columns: {title: string, property: string}[];
  data: T[];
  renderRow: (model: T, index: number) => React.ReactNode;
  defaultSortColumn: keyof RTPMappingProduct | keyof SiriuswareMappingProduct;
  isLoadingData?: boolean;
  /** Rows keep their place while being edited, so a row cannot move away from the field being typed in. */
  isEditing?: boolean;
  infoMsg?: string;
  infoKey?: string;
  /** Columns whose data can fail to load, keyed by property. A value is the red triangle's hover text. */
  columnErrors?: Partial<Record<string, string>>;
  /** Columns of columnErrors still loading, which show a spinner where the triangle goes. */
  loadingColumns?: string[];
}

/**
 * Resort Mapping Products Table
 * renders a dynamic table of either an SW or RTP Mapping Products table
 * state is all handled in redux with actions for sorting and editing
 * @param props
 * @constructor
 */
export const DynamicTable = (props: props<RTPMappingProduct | SiriuswareMappingProduct>) => {

  const dispatch = useDispatch();
  const [sortAscending, setSortAscending] = React.useState<boolean>(true);
  const [sortColumn, setSortColumn] = React.useState<string>(props.defaultSortColumn);

  const handleSort = (property: string) => {
    const newSortAscending = sortColumn !== property || sortColumn === '' || !sortAscending;

    dispatch(sortProductsAction(
      {
        sortBy: property as keyof RTPMappingProduct | keyof SiriuswareMappingProduct,
        sortAscending: newSortAscending,
      }));

    setSortColumn(property);
    setSortAscending(newSortAscending);
  }

  // The store owns row order and a load writes it in server order, so re-sort whenever the rows change.
  // Terminates because sortProductsReducer returns the same state, and so the same props.data, once nothing moves.
  // Held while editing, then catches up once editing ends.
  useEffect(() => {
    if (!props.isLoadingData && !props.isEditing) {
      dispatch(sortProductsAction(
        {
          sortBy: sortColumn as keyof RTPMappingProduct | keyof SiriuswareMappingProduct,
          sortAscending: sortAscending,
        }));
    }
  },[dispatch, props.data, props.isEditing, props.isLoadingData, sortAscending, sortColumn])

  const onTooltipClick = (event) => {
     event.preventDefault();
     event.stopPropagation(); // Prevent info button click from triggering column sort logic
  }
  
  const renderSortingArrow = (
    sortAscending,
    isVisible: boolean,
  ) => <img
    src={downArrow}
    alt={sortAscending ? 'Ascending' : 'Descending'}
    css={sortingArrow(isVisible, sortAscending)}
  />;

  const canFail = (property: string) => !!props.columnErrors && property in props.columnErrors;

  // Hidden, not removed, so the header keeps its width
  const renderColumnStatus = (property: string) => {
    if (!canFail(property)) return null;
    const error = props.columnErrors?.[property];
    return <span css={css`position: relative;`}>
      <span css={css`visibility: ${error ? 'visible' : 'hidden'};`}>
        <TooltipFC
          disabled={!error}
          backgroundColor={`${DARK_TOOLTIP}`}
          contentCss={errorTooltipStyle}
          content={<span>{error}</span>}
          children={<img alt={error ?? ''} src={errorImgRed} css={infoStyle} onClick={onTooltipClick} />}/>
      </span>
      {props.loadingColumns?.includes(property) && <IosSpinner rootCss={columnSpinnerStyle}/>}
    </span>;
  };

  return (
    <div css={css`transition: all .5s ease;`}>
      {
        props.data.length > 0 &&
        <table css={tableStyle}>
          <thead>
          <tr>
            {
              props.columns.map(column => (
                <th
                  key={column.title}
                  onClick={() => { handleSort(column.property); }}
                  scope={'col'}
                  align={'center'}
                  title={column.title}
                  css={css`
                        cursor: ${sortAscending ? 'pointer' : 'auto'};
                        ${UNSELECTABLE_TEXT};
                        white-space: nowrap;
                        ${canFail(column.property) && triangleColumnHeaderStyle};
                      `}
                >
                  <div css={css`display: flex; flex: 1 1 200px;`}>
                    <span css={css`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`}>{column.title}</span>
                    {renderColumnStatus(column.property)}
                    {renderSortingArrow(sortAscending, sortColumn === column.property)}
                    {props.infoKey && column.property === props.infoKey && 
                        <TooltipFC
                          backgroundColor={`${DARK_TOOLTIP}`}
                          content={<span css={css`white-space: pre-wrap;`}>{props.infoMsg}</span>}
                          children={<img alt='ⓘ' src={infoImg} css={infoStyle} onClick={onTooltipClick} />}/>}
                  </div>
                </th>
              ))
            }
          </tr>
          </thead>
          <tbody >
          {
            props.data.map(props.renderRow)
          }
          </tbody>
        </table>
      }
    </div>
  )
};
